import { AfterViewInit, Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DropDownCompanyComponent } from '@sharedComponents/drop-down-company/drop-down-company.component';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ISmartOltConfigCreate } from '@modules/network-manager/pages/smart-olt/definitions/smart-olt.model';
import { CryptoJsService } from '@core/utils/crypto-js/crypto-js.service';
import { SmartOltService } from '@modules/network-manager/services/smart-olt/smart-olt.service';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-config-smart-olt-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, DropDownCompanyComponent, ButtonModule],
  templateUrl: './config-smart-olt-form.component.html',
  styleUrl: './config-smart-olt-form.component.scss',
})
export class ConfigSmartOltFormComponent implements OnInit, AfterViewInit {
  formGroup!: FormGroup;
  public identityMasked = true;
  public passwordMasked = true;
  public deciphered = {
    key: false,
    password: false,
  };

  public loadingSave = false;

  constructor(
    private fb: FormBuilder,
    public readonly dynamicDialogRef: DynamicDialogRef,
    public readonly dynamicDialogConfig: DynamicDialogConfig<{
      id: number;
      config: Partial<ISmartOltConfigCreate>;
      create: boolean;
    }>,
    private readonly cryptoService: CryptoJsService,
    private readonly smartOltService: SmartOltService,
    private readonly confirmationService: ConfirmationService,
    private readonly toastService: ToastrService,
  ) {}
  ngAfterViewInit(): void {
    if (this.dynamicDialogConfig.data?.config) {
      const data: Partial<ISmartOltConfigCreate> = this.dynamicDialogConfig.data.config;
      this.formGroup.get('companyOwnerId')?.setValue(data.companyOwnerId);
    }
  }

  ngOnInit(): void {
    this.createForm();
  }

  /**
   * createForm
   */
  public createForm() {
    this.formGroup = this.fb.group({
      url: new FormControl(null, Validators.compose([Validators.required])),
      key: new FormControl(null, Validators.compose([Validators.required])),
      identity: new FormControl(null, Validators.compose([Validators.required])),
      password: new FormControl(null, Validators.compose([Validators.required])),
      companyOwnerId: new FormControl({ value: null, disabled: true }),
    });
    if (this.dynamicDialogConfig.data?.config) {
      const data: Partial<ISmartOltConfigCreate> = this.dynamicDialogConfig.data.config;
      this.formGroup.patchValue(data);
      this.formGroup.get('companyOwnerId')?.setValue(data.companyOwnerId);
    }
  }

  public async onSubmit() {
    if (this.formGroup.valid) {
      this.confirmationService.confirm({
        message: `¿Está seguro de <strong>${
          this.dynamicDialogConfig.data?.create ? 'crear' : 'actualizar'
        }</strong> los datos de configuracion del Smart OLT?`,
        header: 'Confirmar',
        icon: 'pi pi-exclamation-triangle',
        acceptIcon: 'none',
        rejectIcon: 'none',
        acceptLabel: 'Si',
        rejectLabel: 'No',
        acceptButtonStyleClass: 'p-button-success',
        rejectButtonStyleClass: 'p-button-text',
        accept: () => {
          if (!this.dynamicDialogConfig.data?.create) {
            this.udpate();
          }
          if (this.dynamicDialogConfig.data?.create) {
            this.save();
          }
        },
      });
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  public async toggleIdentityMask(ctrl: 'key' | 'password') {
    if (!this.deciphered[ctrl]) {
      this.formGroup
        .get(ctrl)
        ?.setValue(await this.cryptoService.decipherData(this.formGroup.get(ctrl)?.value));
      this.deciphered[ctrl] = true;
    }
  }

  /**
   * save
   */
  public async udpate() {
    this.loadingSave = true;
    try {
      const body = this.formGroup.getRawValue();
      if (!this.deciphered.key) {
        body.key = await this.cryptoService.decipherData(body.key);
      }
      if (!this.deciphered.password) {
        body.password = await this.cryptoService.decipherData(body.password);
      }

      await this.smartOltService.updateConfigSmartOlt(this.dynamicDialogConfig.data!.id, body);
      this.dynamicDialogRef.close();
      this.toastService.success(`Configuración actualizada correctamente`);
    } catch (error) {
      console.error('Error submitting form', error);
    } finally {
      this.loadingSave = false;
    }
  }

  async save() {
    this.loadingSave = true;
    try {
      const body = this.formGroup.getRawValue();
      await this.smartOltService.createConfigSmartOlt(body);
      this.dynamicDialogRef.close();
      this.toastService.success(`Configuración creada correctamente`);
    } catch (error) {
      console.error('Error submitting form', error);
    } finally {
      this.loadingSave = false;
    }
  }
}
